// Searchable category combobox. Picks a single category from the tree with
// type-to-filter and indented hierarchy. Reusable across /products, POS, forms.

import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { filterTree, flattenWithDepth, type Category } from "./types";

interface Props {
  /** Selected category id; "" means no filter (all categories). */
  value: string;
  onChange: (id: string) => void;
  cats: Category[];
  placeholder?: string;
  className?: string;
}

const ALL_LABEL = "Tất cả danh mục";

export function CategorySelect({
  value,
  onChange,
  cats,
  placeholder = ALL_LABEL,
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // cmdk's built-in filter is disabled (shouldFilter={false}); we reuse the
  // categories feature's filterTree so behaviour (incl. Vietnamese) matches /categories.
  const visible = useMemo(
    () => flattenWithDepth(filterTree(cats, query)),
    [cats, query],
  );

  const selectedLabel = useMemo(() => {
    if (!value) return placeholder;
    return flattenWithDepth(cats).find((n) => n.cat.id === value)?.cat.name ?? placeholder;
  }, [cats, value, placeholder]);

  function pick(id: string) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("h-9 justify-between gap-2 font-normal", className)}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {selectedLabel}
          </span>
          <ChevronsUpDown size={16} className="shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Tìm danh mục…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>Không tìm thấy danh mục.</CommandEmpty>
            <CommandGroup>
              {query.trim() === "" && (
                <CommandItem value="__all__" onSelect={() => pick("")}>
                  <Check size={16} className={cn("mr-2", value ? "opacity-0" : "opacity-100")} />
                  {ALL_LABEL}
                </CommandItem>
              )}
              {visible.map(({ cat, depth }) => (
                <CommandItem
                  key={cat.id}
                  value={cat.id}
                  onSelect={() => pick(cat.id)}
                  style={{ paddingLeft: 8 + depth * 14 }}
                >
                  <Check
                    size={16}
                    className={cn("mr-2", value === cat.id ? "opacity-100" : "opacity-0")}
                  />
                  <span className="truncate">{cat.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
